import { normalize, NormalizedSchema } from "normalizr";
import { AjaxError } from "rxjs/ajax";
import _ from "lodash";

import { createAsyncActions, Nullable } from "@ctra/utils";

import {
  EventEntity,
  EventListQueryParams,
  EventListQueryProps,
  EventSource,
  EventSourceType,
  TimelineEventFormValues
} from "./typings";

import types from "./types";
import schemas from "./schemas";
import { makeEventHash } from "./utils";
import { FarmEntity, SiteEntity } from "../farms";
import { GenericLabelSource } from "../labels";

/**
 * Payload type for the FETCH_EVENTS pending action
 */
export type FetchEventsPendingPayload = {
  /**
   * auto-generated  event hash
   */
  hash: string;
  /**
   * event farm id
   */
  farmID: FarmEntity["id"];
  /**
   * event site id
   */
  siteID: Nullable<SiteEntity["id"]>;
} & EventListQueryProps;

/**
 * Payload type for the FETCH_EVENTS fulfilled action
 */
export type FetchEventsFulfilledPayload = {
  /**
   * auto-generated event hash
   */
  hash: string;
  /**
   * event
   */
  events: Array<EventEntity>;
};

/**
 * Payload type for the FETCH_EVENT rejected action
 */
export type FetchEventsRejectedPayload = {
  /**
   * auto-generated event hash
   */
  hash: string;
  /**
   * event farm id
   */
  farmID: FarmEntity["id"];
  /**
   * event site id
   */
  siteID: Nullable<SiteEntity["id"]>;
  /**
   * Error string for rejection
   */
  error: AjaxError;
};

/**
 * Fetch events
 * @category Action
 */
const fetchEvents = createAsyncActions(
  types.FETCH_EVENTS,
  (farmID: FarmEntity["id"], siteID: Nullable<SiteEntity["id"]>, params?: Partial<EventListQueryProps>) => ({
    hash: makeEventHash(farmID, siteID, params),
    farmID,
    siteID,
    ...params
  }),
  (hash: string, events: Array<EventSource>) => ({
    hash,
    ...normalize(
      _.filter(events, ({ source: { type } }) => {
        return ![EventSourceType.gamePlanTask, EventSourceType.idaQnA].includes(type);
      }),
      schemas.eventList
    )
  }),
  (hash: string, error: string) => ({
    hash,
    error
  })
);

/**
 * Create new event
 * @category Action
 */
const createEvent = createAsyncActions(
  types.CREATE_EVENT,
  (
    formValues: TimelineEventFormValues,
    eventListReference?: Nullable<string>
  ): TimelineEventFormValues & { hash?: Nullable<string> } => ({
    ...formValues,
    hash: eventListReference
  }),
  (
    response: EventSource,
    eventListReference?: Nullable<string>
  ): NormalizedSchema<{ event: EventEntity }, EventEntity["id"]> & { hash?: Nullable<string> } => ({
    ...normalize(response, schemas.event),
    hash: eventListReference
  }),
  (error: unknown) => ({ error })
);

/**
 * Update event
 * @category Action
 */
const updateEvent = createAsyncActions(
  types.UPDATE_EVENT,
  (formValues: TimelineEventFormValues): TimelineEventFormValues => formValues,
  (
    response: EventSource,
    eventListReference: string
  ): NormalizedSchema<{ event: EventEntity }, EventEntity["id"]> & { id: string } => {
    return {
      ...normalize(response, schemas.event),
      id: eventListReference
    };
  },
  (error: unknown) => {
    return { error };
  }
);

/**
 * Delete an existing event
 * @category Action
 */
const deleteEvent = createAsyncActions(
  types.DELETE_EVENT,
  (id: EventEntity["id"], sourceType: EventSourceType, sourceID: GenericLabelSource["GenericLabelID"]) => ({
    id,
    sourceType,
    sourceID
  }),
  (id: EventEntity["id"]) => ({
    id
  }),
  (id: EventEntity["id"], error: string) => ({
    id,
    error
  })
);

export default {
  fetchEvents,
  createEvent,
  updateEvent,
  deleteEvent
};
